import { isInThePast } from '~/server/formatters';
import { AttachmentTypes } from '~/composer/composer/AppConstants';
import { PostValidationError, Services } from '~/constants';
import { getDateString } from '~/server/formatters';
import { gbpChannelDataValidation } from '~/composer/composer/lib/validation/utils/gbpChannelDataValidation';

const getId = (post) => post?.id;

/* eslint-disable camelcase */
const isDraft = (post) => {
  // RPC post logic
  if (post?.isDraft) return true;

  // GraphQL post logic
  if (post?.type === 'draft') return true;
  return false;
};

const isSent = (post) => {
  // RPC post logic
  if (post?.isSent) return true;

  // GraphQL post logic
  if (
    post?.status === 'sent' ||
    post?.status === 'pending_tiktok' ||
    post?.status === 'service'
  )
    return true;

  return false;
};

const isPastReminder = (post) => {
  // RPC post logic
  if (post?.isPastReminder) return true;

  // GraphQL post logic
  if (post.status === 'notified') return true;

  return false;
};

const isPendingTiktok = (post) => {
  // GraphQL post logic
  if (post?.status === 'pending_tiktok') return true;

  return false;
};

const isRetweet = (post) => {
  // RPC post logic
  if (post?.postDetails) return post.postDetails.isRetweet;

  // GraphQL post logic
  return post.retweet !== undefined;
};

const isPastDue = (post) => {
  // RPC post logic
  if (post?.isPastDue !== undefined) return post.isPastDue;

  // GraphQL post logic
  if (post?.dueAt) return isInThePast(post?.dueAt);
  return false;
};

const getScheduledAt = (post) => {
  return post?.scheduledAt || post?.scheduled_at;
};

const getDueAt = (post) => {
  return post?.due_at || post?.dueAt;
};

// It's common to think that the concept of a post
// being scheduled depends on it having a scheduled at
// date, whereas it actually depends on the due at date.
// This helper aims to help abstract that confusion.
const isScheduled = (post) => {
  return !!getDueAt(post);
};

const getFirstComment = (post) => {
  // RPC post logic
  if (post?.commentEnabled) return post?.commentText;

  // GraphQL post logic
  const graphFirstComment = post?.attachments?.find(
    (attachment) => attachment?.__typename === 'PublishingPostFirstComment'
  );
  if (graphFirstComment) return graphFirstComment?.text;
};

const needsApproval = (post) => {
  if (isDraft(post) && post.needsApproval) return true;
  return false;
};

const userIsPostOwner = (post, userId) => {
  if (!post || !userId) return false;

  // RPC post logic
  if (post?.user?.id === userId) return true;

  // GraphQL post logic
  if (post?.createdBy === userId) return true;

  return false;
};

const hasText = (post) => {
  return !!post?.text;
};

const hasImage = (post) => {
  // RPC post logic
  if (post?.media?.picture) return true;

  // GraphQL post logic
  if (
    Array.isArray(post?.media) &&
    post?.media?.some((item) => item?.type === 'image')
  )
    return true;

  return false;
};

const hasVideo = (post) => {
  // RPC post logic
  if (post?.media?.video?.id) return true;

  // GraphQL post logic
  if (
    Array.isArray(post?.media) &&
    post?.media?.some((item) => item.type === 'video')
  )
    return true;

  return false;
};

const hasSourceUrl = (post) => {
  // eslint-disable-next-line camelcase
  return !!post?.source_url || !!post?.sourceUrl;
};

const getChannelId = (post) => {
  // RPC || GraphQL
  return post?.profileId || post?.channel?.id;
};

const getChannelName = (post) => {
  // RPC || GraphQL
  return post?.profile_service || post?.channel?.service;
};

const getChannel = (post) => {
  const channelName = getChannelName(post);
  if (channelName) return Services.get(channelName);
};

const validatePost = (post) => {
  const channel = getChannel(post);

  if (
    !hasImage(post) &&
    !hasVideo(post) &&
    channel?.requiredAttachmentType === AttachmentTypes.MEDIA
  ) {
    return PostValidationError.MISSING_IMAGE_OR_VIDEO;
  }

  if (!hasSourceUrl(post) && !hasVideo(post) && channel?.requiresSourceUrl) {
    return PostValidationError.MISSING_SOURCE_URL;
  }

  if (!hasText(post) && channel.requiresText) {
    return PostValidationError.MISSING_TEXT;
  }

  if (
    (channel?.name === 'googlebusiness' ||
      channel?.service === 'googlebusiness') &&
    /**
     * TODO: It's a hotfix for GBP missing channelData when created through Zapier.
     * A proper fix is to add this on save for the API endpoint (on the backend)
     */
    post.channelData
  ) {
    const validationErrors = gbpChannelDataValidation.validateGBPChannelData(
      post.updateType,
      post.channelData?.googlebusiness
    );
    if (validationErrors.length > 0) {
      return validationErrors[0].message;
    }
  }

  return null;
};

const isPostEntity = (post) => {
  return post?.__typename === 'PublishingPost';
};

const hasCampaign = (post) => {
  return !!(post?.campaign || post?.campaignDetails);
};

const getCampaign = (post) => {
  return post?.campaignDetails || post?.campaign;
};

const isCustomScheduled = (post) => {
  // RPC post logic
  if (post?.postDetails) return post.postDetails.isCustomScheduled;

  // GraphQL post logic
  return post?.isCustomScheduled || !!post.scheduled_at;
};

const isInstagramReminder = (post) => {
  // RPC post logic
  if (post?.postDetails) return post.postDetails.isInstagramReminder;

  // GraphQL post logic
  return (
    post?.isInstagramReminder ||
    (post.profile_service === 'instagram' && !post.can_send_direct)
  );
};

const getFormatedCreatedAt = (post, { twentyFourHourTime, timezone }) => {
  const createdAt = post?.createdAt;
  return getDateString(createdAt, timezone, {
    createdAt,
    twentyFourHourTime,
  });
};

const getError = (post) => {
  if (post?.postDetails) {
    return post.postDetails.error;
  }

  const status = post?.status;
  if (status !== 'error') {
    return null;
  }

  const error = post?.error;
  const isObject = typeof error === 'object' && error !== null;
  return isObject ? error.text || '' : error || '';
};

const hasError = (post) => !!getError(post);

const getErrorLink = (post) => {
  // RPC post logic
  if (post?.postDetails) return post.postDetails.errorLink;

  // GraphQL post logic
  return post.status === 'error' && post.error && post.error.link
    ? post.error.link
    : null;
};

const getErrorLabel = (post) => {
  // RPC post logic
  if (post?.postDetails) return post.postDetails.errorLabel ? post.postDetails.errorLabel : 'Learn More';

  // GraphQL post logic
  return post.status === 'error' && post.error && post.error.link_text
    ? post.error.link_text
    : 'Learn More';
};

export const PostEntity = {
  getId,
  isDraft,
  isSent,
  isPastReminder,
  isPastDue,
  isPendingTiktok,
  isRetweet,
  getScheduledAt,
  getDueAt,
  isScheduled,
  getFirstComment,
  needsApproval,
  userIsPostOwner,
  hasText,
  hasImage,
  hasVideo,
  hasSourceUrl,
  getChannelId,
  getChannelName,
  getChannel,
  validatePost,
  isPostEntity,
  hasCampaign,
  getCampaign,
  isCustomScheduled,
  isInstagramReminder,
  getFormatedCreatedAt,
  getError,
  hasError,
  getErrorLink,
  getErrorLabel,
};
